import { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Table, TableBody, TableCell, TableHead, TableRow,
  IconButton, LinearProgress, Alert
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { ApiRequest } from 'GlobalFunctions';
import AddSiblingProductModal from './AddSiblingProductModal';

export interface SubstituteItem {
  product_id: number;
  product_name: string;
  product_code?: string;
  quantity: number;
}

interface Props {
  open: boolean;
  onClose: () => void;
  orderId?: number; // optional for batch mode
  product: { product_id: number; product_name: string; quantity: number };
  warehouseId: string;
  availableStock: number;
  orderProducts: { product_id: number; quantity: number }[];
  initialItems?: SubstituteItem[]; // for batch mode: pre-loaded items
  onSaved: (items?: SubstituteItem[]) => void; // in batch mode, receives items
}

export default function SubstitutionDialog({ open, onClose, orderId, product, warehouseId, availableStock, orderProducts, initialItems, onSaved }: Props) {
  const [items, setItems] = useState<SubstituteItem[]>([]);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [confirmIncomplete, setConfirmIncomplete] = useState(false);
  const isBatch = !orderId;

  useEffect(() => {
    if (!open) return;
    if (isBatch) {
      // Batch mode: use initialItems or default
      if (initialItems && initialItems.length) {
        setItems(initialItems);
      } else {
        const stock = Math.max(0, availableStock);
        setItems(stock > 0 ? [{ product_id: product.product_id, product_name: product.product_name, quantity: stock }] : []);
      }
      return;
    }
    // Single mode: load from server
    ApiRequest({
      url: '/orders/substitutions/get',
      method: 'post',
      headers: { ltkn: localStorage.getItem('ltkn') },
      body: { order_id: orderId },
      setResponse: (r: any) => {
        if (r.status === 'success' && r.substitutions[product.product_id]) {
          setItems(r.substitutions[product.product_id].map((s: any) => ({
            product_id: s.substitute_product_id,
            product_name: s.product_name,
            product_code: s.product_code,
            quantity: s.quantity,
          })));
        } else {
          const stock = Math.max(0, availableStock);
          setItems(stock > 0 ? [{ product_id: product.product_id, product_name: product.product_name, quantity: stock }] : []);
        }
      },
    });
  }, [open]);

  const totalAssigned = items.reduce((sum, i) => sum + i.quantity, 0);
  const progress = Math.min(100, (totalAssigned / product.quantity) * 100);

  const handleRemove = (idx: number) => setItems(items.filter((_, i) => i !== idx));

  const handleAdd = (p: { product_id: number; product_name: string; product_code: string; quantity: number }) => {
    const existing = items.findIndex(i => i.product_id === p.product_id);
    if (existing >= 0) {
      const updated = [...items];
      updated[existing] = { ...updated[existing], quantity: updated[existing].quantity + p.quantity };
      setItems(updated);
    } else {
      setItems([...items, p]);
    }
  };

  const save = () => {
    if (isBatch) {
      onSaved(items);
      onClose();
      return;
    }
    ApiRequest({
      url: '/orders/substitutions/save',
      method: 'post',
      headers: { ltkn: localStorage.getItem('ltkn') },
      body: {
        order_id: orderId,
        original_product_id: product.product_id,
        original_quantity: product.quantity,
        warehouse_id: warehouseId,
        substitutions: items.map(i => ({ product_id: i.product_id, quantity: i.quantity })),
      },
      setResponse: (r: any) => {
        if (r.status === 'success') { onSaved(); onClose(); }
      },
    });
  };

  const handleConfirm = () => {
    if (totalAssigned < product.quantity) setConfirmIncomplete(true);
    else save();
  };

  return (<>
    <Dialog open={confirmIncomplete} onClose={() => setConfirmIncomplete(false)}>
      <DialogTitle>Cantidad incompleta</DialogTitle>
      <DialogContent>
        <Alert severity="warning">No estás completando la cantidad total ({totalAssigned}/{product.quantity}). ¿Deseas confirmar de todas formas?</Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setConfirmIncomplete(false)}>Cancelar</Button>
        <Button variant="contained" color="warning" onClick={() => { setConfirmIncomplete(false); save(); }}>Confirmar</Button>
      </DialogActions>
    </Dialog>

    <AddSiblingProductModal
      open={addModalOpen}
      onClose={() => setAddModalOpen(false)}
      productId={product.product_id}
      warehouseId={warehouseId}
      orderProducts={orderProducts.filter(op => op.product_id !== product.product_id)}
      onAdd={handleAdd}
      existingSubstitutes={items}
      remainingToAssign={Math.max(0, product.quantity - totalAssigned)}
    />

    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Sustituir: {product.product_name}</DialogTitle>
      <DialogContent>
        <div style={{ marginBottom: 12 }}>
          <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 4 }} />
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#555' }}>{totalAssigned} / {product.quantity} unidades asignadas</p>
        </div>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Producto</TableCell>
              <TableCell align="center">Cantidad</TableCell>
              <TableCell align="center" width={50}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item, idx) => (
              <TableRow key={`${item.product_id}-${idx}`}>
                <TableCell>{item.product_name} {item.product_code && <span style={{ color: '#888', fontSize: 12 }}>{item.product_code}</span>}</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>{item.quantity}</TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => handleRemove(idx)}><DeleteIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
            {!items.length && <TableRow><TableCell colSpan={3} align="center" sx={{ color: '#888' }}>Sin productos asignados</TableCell></TableRow>}
          </TableBody>
        </Table>
        <div style={{ textAlign: 'center', marginTop: 12 }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddModalOpen(true)} disabled={totalAssigned >= product.quantity}>Agregar producto</Button>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={handleConfirm} disabled={items.length === 0}>Confirmar cambio</Button>
      </DialogActions>
    </Dialog>
  </>);
}
